import {Expressions} from '@packages/systems/wfdl';
import {EText, type Expression} from '@packages/systems/wfdl-ast';
import {$, at} from '@packages/utilities/wfdl-helpers';
import {isPopoverAction, type PopoverAction} from './actions';
import {getOverlayTargetReferenceParts} from '../shared/expression';

const POPOVER_TARGET_CONTEXT_KEY = 'popoverTarget';

export type PopoverTargetReference = {
  componentKey: string;
  targetId: string;
  action: PopoverAction;
};

export const getPopoverTargetReference = (
  expression: Expression
): PopoverTargetReference | null => {
  const reference = getOverlayTargetReferenceParts(
    expression,
    POPOVER_TARGET_CONTEXT_KEY
  );
  if (!reference || !isPopoverAction(reference.action)) return null;
  return {...reference, action: reference.action};
};

/** Reads durable Popover targets while normalizing missing or unknown actions. */
export const getPopoverTargetReferenceForRead = (
  expression: Expression
): PopoverTargetReference | null => {
  const reference = getOverlayTargetReferenceParts(
    expression,
    POPOVER_TARGET_CONTEXT_KEY
  );
  if (!reference) return null;
  return {
    ...reference,
    action: isPopoverAction(reference.action) ? reference.action : 'toggle',
  };
};

export const createPopoverTargetExpression = ({
  componentKey,
  targetId,
  action,
}: PopoverTargetReference): Expression =>
  Expressions._call(at($('context'), [POPOVER_TARGET_CONTEXT_KEY]), [
    EText(componentKey),
    EText(targetId),
    EText(action),
  ]);

/**
 * Rewrites every Popover target reference in an expression through an
 * old id -> new id map.
 *
 * Both stored parts of a reference are element ids: `targetId` is the target
 * element, and `componentKey` is the owning component's name, which matches
 * its render root element id (the page uses the `page` key instead). When a
 * library component render is instantiated with fresh local ids, references
 * still point at the origin ids and no longer resolve, so they have to be
 * remapped alongside the render.
 *
 * Ids missing from the map are preserved, as is the action.
 */
export const remapPopoverTargetReferences = (
  expression: Expression,
  idMap: {[oldId: string]: string}
): Expression =>
  Expressions.everywhere((expr) => {
    const reference = getPopoverTargetReference(expr);
    if (!reference) return expr;

    const componentKey = idMap[reference.componentKey];
    const targetId = idMap[reference.targetId];
    if (componentKey == null && targetId == null) return expr;

    return createPopoverTargetExpression({
      ...reference,
      componentKey: componentKey ?? reference.componentKey,
      targetId: targetId ?? reference.targetId,
    });
  })(expression);

/**
 * Rewrites valid Popover references anywhere in an expression. Callers that
 * process each element independently can stop at descendant element data.
 */
export const rewritePopoverTargetReferences = (
  expression: Expression,
  rewrite: (
    reference: PopoverTargetReference,
    expression: Expression
  ) => Expression,
  {traverseElements = true}: {traverseElements?: boolean} = {}
): Expression => {
  const rewriteReference = (currentExpression: Expression) => {
    const reference = getPopoverTargetReference(currentExpression);
    return reference
      ? rewrite(reference, currentExpression)
      : currentExpression;
  };

  if (traverseElements) {
    return Expressions.everywhere(rewriteReference)(expression);
  }

  const referencePaths = Expressions.findPathsWithStoppable(
    expression,
    (currentExpression) => {
      if (Expressions.isElement(currentExpression)) {
        return Expressions.stop(false);
      }
      if (getPopoverTargetReference(currentExpression)) {
        return Expressions.stop(true);
      }
      return Expressions.resume(false);
    }
  );

  return referencePaths.reduce(
    (currentExpression, path) =>
      Expressions.updateIn(currentExpression, path, rewriteReference),
    expression
  );
};
